import { createContext, useContext, useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import type { PermissionKey } from "@bookdental/schemas";
import { AuthProvider, useAuth, type SessionUser } from "@/lib/auth";
import { createQueryClient, createTRPCClient } from "@/lib/trpc-client";
import { useAbrirNaOrganizacao } from "@/lib/use-abrir-na-organizacao";

/** Verdadeiro dentro de um painel de OUTRA organização — botão que navega pra uma página abriria a
 *  organização ativa, não a do painel, então quem navega se esconde. */
const EmOutraOrganizacao = createContext(false);
export const useEmOutraOrganizacao = () => useContext(EmOutraOrganizacao);
const SoLeitura = createContext(false);
export const useSoLeitura = () => useContext(SoLeitura);
/** Ir para uma tela desta organização de verdade (troca a ativa, com "Desfazer"). Precisa ser
 *  montado aqui, por fora do foco: lá dentro a "ativa" que o useAuth enxerga já é a do foco, e o
 *  useAbrirNaOrganizacao só navegaria, sem trocar. Nulo fora de um foco. */
const IrParaOFoco = createContext<((nome: string, href: string) => void) | null>(null);
export const useIrParaOFoco = () => useContext(IrParaOFoco);

/** Ficha de outra clínica aberta pelo acesso pessoal: vê tudo que o papel deixa ver, mas só agenda
 *  e pede prótese. As permissões de escrita somem da tela e o cliente recusa o resto. */
const PERMISSOES_DO_SO_LEITURA: PermissionKey[] = ["agenda.manage", "prosthesis.manage"];
const MUTACOES_DO_SO_LEITURA = ["appointment.create", "prosthesis.create", "prosthesis.setSentChannels", "file.attachToEntity", "me"];

function soLeitura(u: SessionUser): SessionUser {
  const org = u.activeOrganization;
  if (!org) return u;
  const permissions = Object.fromEntries(
    Object.entries(org.permissions).map(([k, v]) => [k, v && (/\.view(All)?$/.test(k) || PERMISSOES_DO_SO_LEITURA.includes(k as PermissionKey))]),
  ) as NonNullable<SessionUser["activeOrganization"]>["permissions"];
  return { ...u, activeOrganization: { ...org, permissions, canManageTeam: false } };
}

/**
 * Faz tudo que está dentro agir em `organizationId`, sem trocar a organização ativa.
 *
 * Cliente tRPC próprio (cada chamada leva `x-organizacao`, e o servidor usa essa organização como a
 * ativa só naquela requisição) e cache próprio — senão os pacientes de uma clínica apareceriam,
 * em cache, na outra. O `AuthProvider` de dentro relê `me.current` por esse cliente, então
 * `useAuth`/`usePermissao` respondem com o papel e as permissões NESTA organização.
 *
 * Quem usa precisa montar com `key={organizationId}`: cliente e cache nascem uma vez por organização.
 */
export function OrganizacaoEmFoco({ organizationId, somenteLeitura, children }: { organizationId: string; somenteLeitura?: boolean; children: ReactNode }) {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const { abrir } = useAbrirNaOrganizacao();
  const [cliente] = useState(() => createTRPCClient(organizationId, somenteLeitura ? MUTACOES_DO_SO_LEITURA : undefined));
  // Mutação feita no painel muda os números da prévia, que vivem no cache da tela principal.
  const [cache] = useState(() =>
    createQueryClient(() => {
      utils.organization.getOrgPreview.invalidate({ organizationId });
      utils.painelDasOrganizacoes.invalidate();
      // A agenda de fora (o /inicio da Pessoal mostra as consultas das outras clínicas).
      utils.appointment.invalidate();
    }),
  );

  if (user?.activeOrganization?.id === organizationId) return <>{children}</>;

  return (
    <trpc.Provider client={cliente} queryClient={cache}>
      <QueryClientProvider client={cache}>
        <AuthProvider ajustar={somenteLeitura ? soLeitura : undefined}>
          <IrParaOFoco.Provider value={(nome, href) => abrir(organizationId, nome, href)}>
          <EmOutraOrganizacao.Provider value>
            <SoLeitura.Provider value={!!somenteLeitura}>{children}</SoLeitura.Provider>
          </EmOutraOrganizacao.Provider>
          </IrParaOFoco.Provider>
        </AuthProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
}
